import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { MappingLevel, TableLevelState } from '../types/mappingDrillDown';

export interface MappingStackState {
  stack: MappingLevel[];
  currentIndex: number;
  tableStateByLevelId: Record<string, TableLevelState>;

  pushLevel: (level: MappingLevel) => void;
  goToLevel: (index: number) => void;
  clearFromLevel: (index: number) => void;
  /** Keeps only the root level (e.g. Results); removes drill-down levels. */
  collapseToRootLevel: () => void;
  setTableStateForLevel: (levelId: string, state: Partial<TableLevelState>) => void;
  getTableStateForLevel: (levelId: string) => TableLevelState | undefined;
  resetStack: (baseLevel: MappingLevel) => void;
}

export const useMappingStackStore = create<MappingStackState>()(
  devtools(
    (set, get) => ({
      stack: [],
      currentIndex: 0,
      tableStateByLevelId: {},

      pushLevel: level =>
        set(state => {
          const newStack = state.stack.slice(0, state.currentIndex + 1);
          newStack.push(level);
          return {
            stack: newStack,
            currentIndex: newStack.length - 1,
          };
        }),

      goToLevel: index =>
        set(state => ({
          currentIndex: Math.max(0, Math.min(index, state.stack.length - 1)),
        })),

      clearFromLevel: index =>
        set(state => {
          const newStack = state.stack.slice(0, index);
          const newIndex = Math.min(state.currentIndex, newStack.length - 1);
          const removedIds = state.stack.slice(index).map(l => l.id);
          const tableStateByLevelId = { ...state.tableStateByLevelId };
          removedIds.forEach(id => delete tableStateByLevelId[id]);
          return {
            stack: newStack,
            currentIndex: Math.max(0, newIndex),
            tableStateByLevelId,
          };
        }),

      collapseToRootLevel: () => {
        const { stack } = get();
        if (stack.length <= 1) return;
        get().clearFromLevel(1);
      },

      setTableStateForLevel: (levelId, state) =>
        set(prev => ({
          tableStateByLevelId: {
            ...prev.tableStateByLevelId,
            [levelId]: {
              ...prev.tableStateByLevelId[levelId],
              ...state,
            },
          },
        })),

      getTableStateForLevel: levelId => {
        return get().tableStateByLevelId[levelId];
      },

      resetStack: baseLevel =>
        set({
          stack: [baseLevel],
          currentIndex: 0,
          tableStateByLevelId: {},
        }),
    }),
    { name: 'mapping-stack-store' },
  ),
);

export const useCurrentMappingLevel = () =>
  useMappingStackStore(state => {
    const { stack, currentIndex } = state;
    return stack[currentIndex] ?? null;
  });

export const useMappingStackBreadcrumb = () =>
  useMappingStackStore(
    useShallow(state => ({
      stack: state.stack,
      currentIndex: state.currentIndex,
      goToLevel: state.goToLevel,
      clearFromLevel: state.clearFromLevel,
    })),
  );
